import React, { useEffect, useState } from "react";
import styled, { keyframes } from "styled-components";
import { CartIcon } from "@vinomofo/icons";
import GlowingDot from "../glowing-dot/GlowingDot";
import { Box } from "@vinomofo/components";
import { Link } from "gatsby";
import algoliasearch from "algoliasearch";
import useSocialProof, { SocialProofType } from "../../hooks/use-social-proofs";
import useScreenSize from "../../hooks/use-screen-size";

const THIRTY_MINUTES = 30 * 60 * 1000;
const client = algoliasearch(
  process.env.GATSBY_ALGOLIA_APP_ID!,
  process.env.GATSBY_ALGOLIA_SEARCH_KEY!
);
const index = client.initIndex(
  `page-views-stats-${process.env.GATSBY_VM_MARKET?.toLowerCase()}`
);

const getFirstName = (fullName: string) => fullName?.split(" ")[0];
const trimText = (text: string | undefined, maxLength: number) => {
  if (!text) return "";
  if (text?.length <= maxLength) return text;
  return text?.slice(0, maxLength - 3) + "...";
};
interface MofoSocialProofsProps {
  eventEndTime?: Date;
  eventStartTime?: Date;
  eventOffers: Array<{
    offer: {
      tiersEligible?: string[];
      inventoryStatus?: string;
      skuCode?: string;
      route?: {
        slug?: string;
        path?: string;
      };
      name?: string;
    };
  }>;
}

interface SocialProofItem {
  Icon: React.ComponentType<any>;
  label: string;
  stats: string;
  LinkText?: React.ComponentType;
}

interface PageViewResult {
  slug: string;
  visitor_count: number;
  last_accessed_timestamp: string;
  objectID: string;
}

const fadeInOut = keyframes`
  0% { opacity: 0; }
  20% { opacity: 1; }
  80% { opacity: 1; }
  100% { opacity: 0; }
`;

const EmptyIcon = () => <></>;

const SocialProofsContainer = styled(Box)`
  background-color: #ffffff;
  padding: 0;
`;

const SocialProofsInner = styled(Box)`
  margin-left: auto;
  margin-right: auto;
  max-width: 1140px;
  position: relative;
  min-height: 30px;
`;

const SocialProof = styled(Box)`
  padding: 20px 0 0;
  color: #333333;
  align-items: center;
  justify-content: center;
  text-align: center;
  transition: opacity 1s ease-in-out;
  position: absolute;
  display: flex;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  opacity: 0;
  pointer-events: none;
  &.active {
    opacity: 1;
    animation: ${fadeInOut} 6s linear infinite;
    pointer-events: auto;
  }
  &:hover {
    opacity: 1;
  }
  @media ${({ theme }) => theme.mq.max.tablet} {
    padding: 10px 0 0;
  }
`;

const SocialProofIcon = styled(Box)`
  width: 20px;
  height: 20px;
  line-height: 20px;
  margin-right: 5px;
  margin-left: 5px;

  svg {
    width: 20px;
    height: 20px;
    fill: ${({ theme }) => theme.colors.primary};
  }
`;

const SocialProofText = styled(Box)`
  font-size: 12px;
  font-family: Graphik, sans-serif;
  font-weight: 600;
  padding: 1px 0;
  line-height: 18px;
  color: #626468;

  strong {
    color: #48a90f;
    font-weight: bold;
  }
  a {
    color: inherit;
    text-decoration: underline;
  }
  @media ${({ theme }) => theme.mq.max.tablet} {
    font-size: 10px;
    font-weight: 400;
  }
`;

export const MofoSocialProofs = ({
  eventEndTime,
  eventStartTime,
  eventOffers,
}: MofoSocialProofsProps) => {
  const [activeItem, setActiveItem] = useState(0);
  const [pageViews, setPageViews] = useState(0);
  const [remainingTime, setRemainingTime] = useState<
    string | React.ReactNode | null
  >(null);

  const isEventStarted =
    !eventStartTime || new Date().getTime() >= eventStartTime.getTime();

  const skus = eventOffers
    ?.map((eventOffer) =>
      eventOffer.offer?.inventoryStatus !== "SOLD_OUT"
        ? {
            skuCode: eventOffer.offer?.skuCode,
            path: eventOffer.offer?.route?.path ?? "",
            name: eventOffer.offer?.name || "",
          }
        : null
    )
    .filter((sku) => sku !== null);

  const skuCodes = skus
    ?.map((sku) => sku.skuCode)
    .filter((code) => code !== undefined);

  const { socialProof } = useSocialProof(skuCodes || []);
  const [allSocialProofs, setAllSocialProofs] = useState<SocialProofItem[]>([]);
  const { isMobile } = useScreenSize();

  const isValidSocialProof = (socialProofData: SocialProofType) => {
    const matchingOffer = eventOffers?.find(
      (offer) => offer.offer?.name === socialProofData?.name
    );
    if (!matchingOffer && eventOffers?.length) {
      return false;
    }
    return true;
  };

  useEffect(() => {
    const newAllSocialProofs = [
      ...(socialProof &&
      socialProof?.userName &&
      !socialProof?.userName?.includes("undefined") &&
      isValidSocialProof(socialProof)
        ? [
            {
              Icon: CartIcon,
              label: `${getFirstName(
                socialProof.userName
              )?.toUpperCase()} JUST PURCHASED - `,
              LinkText: () => (
                <Link
                  to={`${
                    skus?.find((sku) => sku.skuCode === socialProof?.code)
                      ?.path || ""
                  }`}
                >
                  {trimText(
                    socialProof?.name?.toUpperCase(),
                    isMobile ? 30 : 50
                  ) ||
                    trimText(
                      skus?.find((sku) => sku.skuCode === socialProof?.code)
                        ?.name,
                      isMobile ? 30 : 50
                    )?.toUpperCase() ||
                    ""}
                </Link>
              ),
              stats: "",
            },
          ]
        : []),
      ...(pageViews >= 10
        ? [
            {
              Icon: GlowingDot,
              label: "PEOPLE ARE CURRENTLY ATTENDING THIS EVENT",
              stats: pageViews.toLocaleString(),
            },
          ]
        : []),
      ...(remainingTime
        ? [
            {
              Icon: EmptyIcon,
              label: remainingTime,
              stats: "",
            },
          ]
        : []),
    ];

    setAllSocialProofs(newAllSocialProofs as SocialProofItem[]);
  }, [socialProof, pageViews, remainingTime]);

  useEffect(() => {
    const fetchPageViews = async () => {
      try {
        const result = await index.getObject<PageViewResult>(
          window.location.href
        );
        const lastAccessedTimestamp = new Date(
          result?.last_accessed_timestamp
        ).getTime();
        const now = Date.now();
        if (now - lastAccessedTimestamp <= THIRTY_MINUTES) {
          setPageViews(result?.visitor_count || 0);
        } else {
          setPageViews(0);
        }
      } catch (error) {
        console.error("Error fetching page views:", error);
        setPageViews(0);
      }
    };

    fetchPageViews();
  }, []);

  useEffect(() => {
    if (eventEndTime) {
      const updateRemainingTime = () => {
        const now = new Date();
        const timeDiff = eventEndTime.getTime() - now.getTime();

        if (timeDiff <= 0) {
          setRemainingTime("EVENT HAS ENDED");
        } else if (timeDiff < 24 * 60 * 60 * 1000) {
          const hours = Math.floor(timeDiff / (1000 * 60 * 60));
          const minutes = Math.floor(
            (timeDiff % (1000 * 60 * 60)) / (1000 * 60)
          );
          setRemainingTime(
            <>
              EVENT ENDS IN{" "}
              <strong>
                {hours} HOURS AND {minutes} MINUTES
              </strong>
            </>
          );
        }
      };

      updateRemainingTime();
      const timeInterval = setInterval(updateRemainingTime, 60000);
      return () => clearInterval(timeInterval);
    }
  }, [eventEndTime]);

  useEffect(() => {
    const interval = setInterval(() => {
      setActiveItem((prevItem) =>
        allSocialProofs.length > 0 ? (prevItem + 1) % allSocialProofs.length : 0
      );
    }, 6000);

    return () => clearInterval(interval);
  }, [allSocialProofs]);

  return (
    <SocialProofsContainer>
      <SocialProofsInner>
        {isEventStarted &&
          allSocialProofs.map(({ Icon, label, stats, LinkText }, index) => (
            <SocialProof
              key={index}
              className={activeItem === index ? "active" : ""}
            >
              <SocialProofIcon>
                <Icon size="10px" />
              </SocialProofIcon>
              <SocialProofText>
                <strong>{stats}</strong> {label}
                {LinkText && <LinkText />}
              </SocialProofText>
            </SocialProof>
          ))}
      </SocialProofsInner>
    </SocialProofsContainer>
  );
};
